import {
  ATTRIBUTE_EFFECTS,
  LEGEND,
  legendChapters,
  NINE_SWORDS,
  ZONES,
  ATTRIBUTE_LABELS,
  DAMAGE_TYPE_LABELS,
  ELEMENT_INFO,
  NEIGONG_CATEGORY_LABELS,
  RAGE,
  attributesFor,
  deriveStats,
  equipmentStats,
  FORGE_ELEMENT_INFO,
  FORGE_ELEMENTS,
  FORGE_MAX,
  FORGE_SLIP_FROM,
  forgeStep,
  forgeTier,
  fullStats,
  INFUSE_MAX,
  infuseStep,
  itemName,
  UNFORGED,
  itemOf,
  ITEMS,
  sellPrice,
  SHOP,
  MALL,
  TOPUP_MILESTONES,
  ARENA,
  ARENA_REWARDS,
  arenaPower,
  arenaReward,
  arenaStats,
  type ArenaBuild,
  LADDERS,
  LADDER_IDS,
  ladderFor,
  tierAt,
  type LadderId,
  trainSeconds,
  type TrainKind,
  PASS_TIERS,
  passLevel,
  SEASON,
  TITLES,
  type PassReward,
  type PassTrack,
  TOPUP_PACKS,
  topUpJade,
  RARITY_LABELS,
  SLOT_LABELS,
  type ItemStats,
  type ItemDef,
  type EquipSlot,
  BAG_SIZE,
  getArt,
  getSchool,
  getSkill,
  MARTIAL_ARTS,
  MAX_SKILL_LEVEL,
  NEIGONG,
  neigongLearnBlock,
  getNeigong,
  neigongCost,
  getSkin,
  badgeClaimed,
  badgeTitleName,
  KARMA_LABELS,
  getMount,
  TITLE_LIST,
  plateOf,
  SKIN_SLOT_LABELS,
  STATUSES,
  rankFor,
  rankThreshold,
  SCHOOLS,
  skillCost,
  skillPowerMult,
  type Attributes,
  type PlayerCommand,
  type World,
} from '@nine/shared';
import {
  artIcon,
  currencyIcon,
  elementIcon,
  iconEl,
  itemIcon,
  mountIcon,
  neigongIcon,
  schoolIcon,
  skillIcon,
  statusIcon,
  titleIcon,
} from '../render/icons';
import { plateEl } from '../plates/dom';
import { artH, artW, artX, artY } from '../render/pixel';
import type { SpriteBank } from '../render/sprites';
import type { Checkout } from './checkout';
import { $, asCloseButton, fmtClock, fmtDuration, h, setText } from './dom';
import { badgeSection } from './badge';
import { fittingRoom, newFitting, skinPreview } from './fitting';
import { type AnvilMoment, elementShows, ForgeAnvil } from './forgeAnvil';
import { noteForgeBurst } from '../render/forgeFx';
import { KIND_LABEL } from './hud';
import { qinggongTab } from './qinggong';

export type PanelTab =
  | 'char'
  | 'bag'
  | 'arts'
  | 'qinggong'
  | 'neigong'
  | 'school'
  | 'skin'
  | 'mount'
  | 'mall'
  | 'pass'
  | 'ladder'
  | 'arena'
  | 'shop'
  | 'help'
  | 'legend';

const TABS: [PanelTab, string, string][] = [
  ['char', 'ตัวละคร', 'C'],
  ['bag', 'กระเป๋า', 'I'],
  ['arts', 'วิชายุทธ์', 'K'],
  ['qinggong', 'วิชาตัวเบา', ''],
  ['neigong', 'กำลังภายใน', 'N'],
  ['school', 'สำนัก', 'J'],
  ['mall', 'ร้านหยก', 'B'],
  ['skin', 'สกิน', 'U'],
  ['mount', 'พาหนะ', 'Y'],
  ['pass', 'ตั๋วยุทธภพ', 'P'],
  ['ladder', 'ประลอง', 'L'],
  ['arena', 'ประลองยุทธ', 'O'],
  ['help', 'วิธีเล่น', 'H'],
  ['legend', 'ตำนาน', ''],
];

/**
 * Each window shows only its own group's tabs, so no menu holds everything at once. `glyph` is the
 * brush-written Han heading beside the title (panels-ink.css; it must be in UI_GLYPHS).
 */
const GROUPS: { title: string; glyph: string; tabs: PanelTab[] }[] = [
  { title: 'ตัวละคร', glyph: '武', tabs: ['char', 'bag', 'arts', 'qinggong', 'neigong', 'school'] },
  { title: 'ร้านค้าและรูปลักษณ์', glyph: '衣', tabs: ['mall', 'skin', 'mount', 'pass'] },
  { title: 'ประลอง', glyph: '敵', tabs: ['ladder', 'arena'] },
  { title: 'วิธีเล่นและตำนาน', glyph: '問', tabs: ['help', 'legend'] },
  { title: 'ร้านค้า', glyph: '商', tabs: ['shop'] },
];
const groupOf = (tab: PanelTab) => GROUPS.find((g) => g.tabs.includes(tab))!;

const fmt = (n: number) => Math.floor(n).toLocaleString('th-TH');

/** "「ชุด…」 · หยก 50 · 5,000 ตำลึง" for a pass tier or top-up milestone. */
function rewardLabel(m: PassReward): string {
  return [
    m.title ? `ฉายา「${TITLES[m.title]?.name ?? m.title}」` : '',
    m.skin ? `「${getSkin(m.skin).name}」` : '',
    m.mount ? `「${getMount(m.mount).name}」` : '',
    m.jade ? `หยก ${fmt(m.jade)}` : '',
    m.gold ? `${fmt(m.gold)} ตำลึง` : '',
    m.cultivation ? `ฝึกปรือ ${fmt(m.cultivation)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** An element's icon (16 px) with its name, coloured like the element. */
function elementBadge(el: keyof typeof ELEMENT_INFO): HTMLElement {
  const info = ELEMENT_INFO[el];
  const b = h(
    'span',
    { class: 'elem', title: `ลักษณะ${info.name}` },
    iconEl(elementIcon(el, 16), 'ico16'),
    info.name,
  );
  b.style.setProperty('--el', info.color);
  return b;
}

/** A 16 px icon before a money or points figure: `silver` (ตำลึง), `jade` (หยก), `cultivation`. */
export const moneyIcon = (id: 'silver' | 'jade' | 'cultivation'): HTMLCanvasElement =>
  iconEl(currencyIcon(id, 16), 'ico16');

/** "โจมตี +3 · ป้องกัน +4" style summary of item stats (and a rolled art bonus). */
function statLine(
  s: ItemStats & { arts?: Record<string, number> },
  art?: { id: string; pct: number },
): string {
  const parts: string[] = [];
  if (art) parts.push(`ความเสียหาย${MARTIAL_ARTS[art.id]?.name ?? art.id} +${Math.round(art.pct * 100)}%`);
  for (const [id, pct] of Object.entries(s.arts ?? {}))
    parts.push(`ความเสียหาย${MARTIAL_ARTS[id]?.name ?? id} +${Math.round(pct * 100)}%`);
  if (s.atk) parts.push(`โจมตี +${s.atk}`);
  if (s.def) parts.push(`ป้องกัน +${s.def}`);
  if (s.maxHp) parts.push(`พลังชีวิต +${s.maxHp}`);
  if (s.crit) parts.push(`คริติคอล +${Math.round(s.crit * 1000) / 10}%`);
  if (s.lifesteal) parts.push(`ดูดเลือด ${Math.round(s.lifesteal * 1000) / 10}%`);
  if (s.speed) parts.push(`ความเร็ว +${Math.round(s.speed * 100)}%`);
  return parts.join(' · ') || 'ไม่มีค่าพลัง';
}

function fillBar(ratio: number): HTMLElement {
  const fill = h('i');
  fill.style.width = `${Math.min(100, Math.max(0, ratio * 100))}%`;
  return h('div', { class: 'bar gold' }, fill);
}

/** A list row's 32 px icon. */
const rowIcon = (src: HTMLCanvasElement): HTMLCanvasElement => iconEl(src, 'ico32 li-ico');

/** A detail pane's heading icon: 32 px art at 2×. */
const headIcon = (src: HTMLCanvasElement): HTMLCanvasElement => iconEl(src, 'ico64 head-ico');

/** "โจมตี +2 · ป้องกัน −1" between an item and the one worn in its slot, coloured up or down. */
function statDiff(a: ItemDef, b: ItemDef): HTMLElement {
  const [x, y] = [fullStats(a), fullStats(b)];
  const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
  const rows: [string, number, (v: number) => string][] = [
    ['โจมตี', (x.atk ?? 0) - (y.atk ?? 0), String],
    ['ป้องกัน', (x.def ?? 0) - (y.def ?? 0), String],
    ['พลังชีวิต', (x.maxHp ?? 0) - (y.maxHp ?? 0), String],
    ['คริติคอล', (x.crit ?? 0) - (y.crit ?? 0), pct],
    ['ดูดเลือด', (x.lifesteal ?? 0) - (y.lifesteal ?? 0), pct],
    ['ความเร็ว', (x.speed ?? 0) - (y.speed ?? 0), pct],
  ];
  const parts = rows
    .filter(([, d]) => Math.abs(d) > 1e-9)
    .map(([k, d, f]) =>
      h('span', { class: d > 0 ? 'up' : 'down' }, `${k} ${d > 0 ? '+' : '−'}${f(Math.abs(d))}`),
    );
  return h(
    'div',
    { class: 'cmp' },
    h('small', {}, `เทียบกับ${itemName(b)}ที่สวมอยู่`),
    ...(parts.length ? parts : [h('span', {}, 'ค่าพลังเท่ากัน')]),
  );
}

/** The tabbed menu: character sheet, martial arts training, neigong cultivation, and the how-to-play page. */
export class Panels {
  tab: PanelTab | null = null;
  private lastKey = '';
  /** The fitting room's state: what is being tried on, kept while the panel rebuilds. */
  private readonly fit = newFitting();
  /** Which ladder the ประลอง tab shows (null: the one the character may climb). */
  private ladderView: LadderId | null = null;
  /** What is picked in a list-and-detail tab: an item, an art or a neigong. */
  private pick: Partial<Record<PanelTab, string>> = {};
  /** The forge's anvil (ตีบวก/ตีธาตุ): one canvas kept while the panel rebuilds, animated each frame. */
  private readonly anvil = new ForgeAnvil();

  constructor(
    private readonly world: World,
    private readonly command: (c: PlayerCommand) => void,
    private readonly sprites?: SpriteBank,
    private readonly checkout?: Checkout,
  ) {
    const root = $('#panel');
    root.addEventListener('pointerdown', (e) => e.stopPropagation());
    const close = $('#panelClose');
    asCloseButton(close);
    close.addEventListener('click', () => this.close());
    const tabs = $('#panelTabs');
    tabs.setAttribute('aria-label', 'แท็บ');
    // A key cap only on tabs a key opens directly (วิชาตัวเบา and ตำนาน have none of their own).
    for (const [id, label, key] of TABS) {
      const b = h(
        'button',
        { type: 'button', role: 'tab', data: { tab: id }, title: key ? `${label} (${key})` : label },
        label,
        key ? h('kbd', {}, key) : null,
      );
      b.addEventListener('click', () => this.open(id));
      tabs.append(b);
    }
  }

  toggle(tab: PanelTab): void {
    if (this.tab === tab) this.close();
    else this.open(tab);
  }

  open(tab: PanelTab): void {
    if (tab === 'arena' && !this.world.arenaPicks.length) this.command({ t: 'arenaRefresh' });
    this.tab = tab;
    const group = groupOf(tab);
    $('#panel').hidden = false;
    setText($('#panelTitle'), group.title);
    $('#panelTitle').dataset.glyph = group.glyph;
    $('#panel').setAttribute('aria-label', group.title);
    const tabs = $('#panelTabs');
    tabs.hidden = group.tabs.length < 2;
    for (const b of tabs.querySelectorAll<HTMLButtonElement>('button')) {
      b.hidden = !group.tabs.includes(b.dataset.tab as PanelTab);
      b.setAttribute('aria-selected', String(b.dataset.tab === tab));
    }
    this.lastKey = '';
    this.refresh();
  }

  private select(tab: PanelTab, id: string): void {
    this.pick[tab] = id;
    this.lastKey = '';
    this.refresh();
  }

  /** The player as they look right now (gear and fashion), drawn large. */
  private figure(): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = 120;
    c.height = 150;
    c.className = 'hero-fig';
    const set = this.sprites?.of(this.world.player);
    const img = set?.idle[0];
    if (set && img) {
      const g = c.getContext('2d')!;
      g.imageSmoothingEnabled = false;
      const k = Math.min(136 / artH(img), 3);
      g.drawImage(
        img,
        60 + (artX(img) - set.ax) * k,
        146 + (artY(img) - set.ay) * k,
        artW(img) * k,
        artH(img) * k,
      );
    }
    return c;
  }

  /** A header line over a tab: what it is for on the left, the points to spend on the right. */
  private tabBar(text: string): HTMLElement {
    return h(
      'div',
      { class: 'tab-bar' },
      h('p', {}, text),
      h(
        'span',
        { class: 'purse' },
        moneyIcon('cultivation'),
        'แต้มฝึกปรือ ',
        h('b', {}, fmt(this.world.progress.cultivation)),
      ),
    );
  }

  close(): void {
    this.checkout?.close();
    this.tab = null;
    $('#panel').hidden = true;
  }

  private nextRefresh = 0;

  /** Animate every frame, but compare progress at most ten times a second. */
  refresh(): void {
    if (!this.tab) return;
    const pr = this.world.progress;
    // Keep the anvil smooth while avoiding a full progress serialization on every frame.
    this.anvil.tick(this.world.time);
    const now = performance.now();
    // Opening a tab or selecting an item clears lastKey and must update immediately.
    if (this.lastKey && now < this.nextRefresh) return;
    this.nextRefresh = now + 100;
    const key = `${this.tab}|${this.pick[this.tab] ?? ''}|${Math.floor(pr.cultivation)}|${JSON.stringify([pr.skillLevels, pr.qinggong, pr.neigong, pr.activeArt, pr.activeNeigong, pr.arts, pr.school, pr.inventory, pr.equipment, pr.skins, pr.skinWorn, pr.badges, pr.uniformTier, pr.titles, pr.titleWorn, pr.mounts, pr.mountPick, pr.gold, pr.rolls, pr.jade, pr.topUp, pr.pass, pr.training && [pr.training.kind, pr.training.id, pr.training.level], pr.trainAuto, pr.ladder, pr.karma, this.ladderView, this.world.duel?.phase, pr.arena, this.world.arenaPicks, pr.quest.index])}`;
    // The countdown ticks in place rather than rebuilding the tab every second.
    for (const el of $('#panelBody').querySelectorAll<HTMLElement>('.train-left'))
      setText(el, fmtClock(pr.training?.left ?? 0));
    if (key === this.lastKey) return;
    this.lastKey = key;
    const body = $('#panelBody');
    body.replaceChildren(
      this.tab === 'char'
        ? this.charTab()
        : this.tab === 'bag'
          ? this.bagTab()
          : this.tab === 'arts'
            ? this.artsTab()
            : this.tab === 'qinggong'
              ? qinggongTab(this.world, this.command, this.pick.qinggong, (id) => this.select('qinggong', id))
              : this.tab === 'neigong'
                ? this.neigongTab()
                : this.tab === 'school'
                  ? this.schoolTab()
                  : this.tab === 'skin'
                    ? this.skinTab()
                    : this.tab === 'mount'
                      ? this.mountTab()
                      : this.tab === 'shop'
                        ? this.shopTab()
                        : this.tab === 'mall'
                          ? this.mallTab()
                          : this.tab === 'pass'
                            ? this.passTab()
                            : this.tab === 'ladder'
                              ? this.ladderTab()
                              : this.tab === 'arena'
                                ? this.arenaTab()
                                : this.tab === 'legend'
                                  ? this.legendTab()
                                  : this.helpTab(),
    );
  }

  /** Worn equipment by slot, then everything carried with a button to wear it. */
  /** Bag: worn gear around the figure, the bag as a grid of slots, and the picked item in detail. */
  private bagTab(): HTMLElement {
    const pr = this.world.progress;
    const counts = new Map<string, number>();
    for (const id of pr.inventory) if (itemOf(pr.rolls, id)) counts.set(id, (counts.get(id) ?? 0) + 1);
    const ids = [...counts.keys()];
    const pick = ids.includes(this.pick.bag ?? '') ? this.pick.bag! : (ids[0] ?? null);

    const slotCell = (slot: EquipSlot) => {
      const id = pr.equipment[slot];
      const it = id ? itemOf(pr.rolls, id) : undefined;
      const b = h(
        'button',
        {
          type: 'button',
          class: `eq ${it ? `r-${it.rarity}` : 'empty'} ${id && id === pick ? 'sel' : ''}`,
          title: it ? it.name : `${SLOT_LABELS[slot]}: ว่าง`,
          disabled: !it,
        },
        it ? iconEl(itemIcon(it)) : h('span', { class: 'eq-none' }),
        h('small', {}, SLOT_LABELS[slot]),
      );
      if (id) b.addEventListener('click', () => this.select('bag', id));
      return b;
    };
    const doll = h(
      'section',
      { class: 'doll' },
      h('div', { class: 'doll-col' }, slotCell('head'), slotCell('body')),
      h('div', { class: 'doll-fig' }, this.figure()),
      h('div', { class: 'doll-col' }, slotCell('weapon')),
      h(
        'div',
        { class: 'doll-sum' },
        h('small', {}, 'อุปกรณ์ที่สวมเพิ่ม'),
        statLine(equipmentStats(pr.equipment, pr.rolls)),
      ),
    );

    const grid = h('div', { class: 'bag-grid' });
    for (const id of ids) {
      const it = itemOf(pr.rolls, id)!;
      const n = counts.get(id)!;
      const b = h(
        'button',
        {
          type: 'button',
          class: `bag-cell r-${it.rarity} ${id === pick ? 'sel' : ''}`,
          title: itemName(it),
          ariaPressed: String(id === pick),
        },
        iconEl(itemIcon(it)),
        n > 1 ? h('b', { class: 'cnt' }, String(n)) : null,
        it.forge?.plus
          ? h('b', { class: `plus ${forgeTier(it.forge.plus) ?? ''}` }, `+${it.forge.plus}`)
          : null,
        pr.equipment[it.slot] === id ? h('i', { class: 'worn' }, 'สวม') : null,
      );
      b.addEventListener('click', () => this.select('bag', id));
      grid.append(b);
    }
    for (let k = pr.inventory.length; k < BAG_SIZE; k++) grid.append(h('div', { class: 'bag-cell empty' }));

    return h(
      'div',
      { class: 'tab-inv' },
      doll,
      h(
        'section',
        { class: 'bag-side' },
        h(
          'div',
          { class: 'bag-head' },
          h('h4', {}, 'กระเป๋า'),
          h('small', {}, `${pr.inventory.length}/${BAG_SIZE} ช่อง`),
        ),
        grid,
        this.itemDetail(pick),
      ),
    );
  }

  private itemDetail(id: string | null): HTMLElement {
    const pr = this.world.progress;
    const it = id ? itemOf(pr.rolls, id) : undefined;
    if (!id || !it) return h('div', { class: 'item-detail empty' }, 'กระเป๋าว่าง ปราบศัตรูเพื่อเก็บอุปกรณ์');
    const worn = pr.equipment[it.slot] === id;
    const cur = pr.equipment[it.slot] ? itemOf(pr.rolls, pr.equipment[it.slot]!) : undefined;
    const b = h(
      'button',
      { type: 'button', class: `btn small ${worn ? 'ghost' : ''}`, data: { sfx: 'none' } },
      worn ? 'ถอด' : 'สวมใส่',
    );
    b.addEventListener('click', () =>
      this.command(worn ? { t: 'unequip', slot: it.slot } : { t: 'equip', id }),
    );
    return h(
      'div',
      { class: `item-detail r-${it.rarity}` },
      h(
        'div',
        { class: 'idt-head' },
        iconEl(itemIcon(it), 'icon big'),
        h(
          'div',
          {},
          h('div', { class: 'item-name' }, itemName(it)),
          h(
            'div',
            { class: 'item-meta' },
            `${SLOT_LABELS[it.slot]} · ${RARITY_LABELS[it.rarity]}${worn ? ' · กำลังสวม' : ''}`,
          ),
        ),
        b,
      ),
      h('div', { class: 'item-stats' }, statLine(fullStats(it), it.art)),
      !worn && cur ? statDiff(it, cur) : null,
      h('p', { class: 'note' }, it.desc),
      it.slot === 'weapon' ? this.forgeBox(id, it) : null,
    );
  }

  /**
   * The forge (ตีบวก/ตีธาตุ) for a weapon in the bag: its + with the next strike's price and odds,
   * and the five elements to temper it with.
   */
  private forgeBox(id: string, it: ItemDef): HTMLElement {
    const pr = this.world.progress;
    const f = it.forge ?? UNFORGED;
    const pct = (c: number) => `${Math.round(c * 100)}%`;
    // Forging a shop weapon gives it an id of its own: keep it picked.
    const strike = (cmd: PlayerCommand) => {
      const next = `roll-${pr.nextRoll}`;
      const gold = pr.gold;
      this.command(cmd);
      const now = !pr.inventory.includes(id) && pr.inventory.includes(next) ? next : id;
      if (now !== id) this.select('bag', next);
      // Paid for, so struck: play it on the anvil, and over the player when it took.
      if (pr.gold === gold || (cmd.t !== 'forge' && cmd.t !== 'infuse')) return;
      const after = itemOf(pr.rolls, now)?.forge ?? UNFORGED;
      const at = this.world.time;
      const m: AnvilMoment =
        cmd.t === 'infuse'
          ? {
              what: 'element',
              el: cmd.el,
              ok: after.el === cmd.el && after.lv > (f.el === cmd.el ? f.lv : 0),
              slip: false,
              level: after.el === cmd.el ? after.lv : 0,
              at,
              from: f,
            }
          : {
              what: 'plus',
              el: null,
              ok: after.plus > f.plus,
              slip: after.plus < f.plus,
              level: after.plus,
              at,
              from: f,
            };
      this.anvil.strike(now, m);
      noteForgeBurst({ at, what: m.what, ok: m.ok, level: m.level, el: m.el });
    };
    this.anvil.show(id, f);
    const step = forgeStep(f.plus);
    const tier = forgeTier(f.plus);
    const hammer = h(
      'button',
      { type: 'button', class: 'btn small', disabled: !step || pr.gold < step.cost, data: { sfx: 'none' } },
      step ? `ตีบวก +${f.plus + 1} · ${fmt(step.cost)} ตำลึง` : `+${FORGE_MAX} สูงสุดแล้ว`,
    );
    hammer.addEventListener('click', () => strike({ t: 'forge', id }));
    const elements = FORGE_ELEMENTS.map((el) => {
      const info = FORGE_ELEMENT_INFO[el];
      const lv = f.el === el ? f.lv : 0;
      const st = infuseStep(lv);
      const b = h(
        'button',
        {
          type: 'button',
          class: `forge-el ${f.el === el ? 'on' : ''}`,
          data: { sfx: 'none' },
          disabled: !st || pr.gold < (st?.cost ?? 0),
          title: st
            ? `${info.desc} · ขั้น ${lv + 1}: ${fmt(st.cost)} ตำลึง โอกาส ${pct(st.chance)}${
                f.el && f.el !== el ? ` · ธาตุ${FORGE_ELEMENT_INFO[f.el].name}เดิมจะหายไป` : ''
              }`
            : `${info.desc} · ขั้น ${INFUSE_MAX} สูงสุดแล้ว`,
        },
        h('b', {}, info.name),
        h('small', {}, `${lv}/${INFUSE_MAX}`),
      );
      b.style.setProperty('--el', info.color);
      b.addEventListener('click', () => strike({ t: 'infuse', id, el }));
      return b;
    });
    return h(
      'div',
      { class: 'forge' },
      this.anvil.root,
      h(
        'div',
        { class: 'forge-row' },
        h(
          'div',
          {},
          h('b', { class: `forge-plus ${tier ?? ''}` }, `+${f.plus}`),
          h(
            'small',
            {},
            step
              ? `โอกาสสำเร็จ ${pct(step.chance)}${f.plus >= FORGE_SLIP_FROM ? ' · ล้มเหลวลด 1 ขั้น' : ''}`
              : 'ตีบวกถึงขั้นสูงสุด',
          ),
        ),
        hammer,
      ),
      h(
        'small',
        { class: 'forge-hint' },
        '+1 เงาคมเหล็ก · +4 ประกายคราม · +7 สายฟ้าคราม · +10 สายฟ้าโลหิต · +15 สายฟ้าทมิฬ',
      ),
      h('div', { class: 'forge-els' }, ...elements),
      h(
        'small',
        { class: 'forge-hint' },
        f.el
          ? `ธาตุ${FORGE_ELEMENT_INFO[f.el].name} ขั้น ${f.lv}/${INFUSE_MAX} · ${elementShows(f.lv)}`
          : 'ยังไม่มีธาตุ · ธาตุเรืองบนคมตั้งแต่ขั้น 1 วนรอบอาวุธตั้งแต่ขั้น 5',
      ),
    );
  }

  /**
   * Train a move or neigong: its cost and how long it takes; while it trains, the time left and a
   * stop button. One thing trains at a time.
   */
  private trainButton(kind: TrainKind, id: string, lv: number, maxed: boolean): HTMLElement {
    const pr = this.world.progress;
    const t = pr.training;
    if (t && t.kind === kind && t.id === id) {
      const stop = h('button', { type: 'button', class: 'btn small ghost' }, 'หยุด');
      stop.addEventListener('click', () => this.command({ t: 'trainStop' }));
      const auto = h(
        'button',
        {
          type: 'button',
          class: `btn small ${pr.trainAuto ? '' : 'ghost'}`,
          ariaPressed: String(pr.trainAuto),
          title: 'ฝึกขั้นถัดไปต่อเองจนตัน',
        },
        pr.trainAuto ? 'อัตโนมัติ: เปิด' : 'อัตโนมัติ: ปิด',
      );
      auto.addEventListener('click', () => this.command({ t: 'trainAuto', on: !pr.trainAuto }));
      return h(
        'span',
        { class: 'training-chip' },
        h('span', { class: 'chip on' }, `กำลังฝึกขั้น ${t.level}`),
        h('b', { class: 'train-left' }, fmtClock(t.left)),
        auto,
        stop,
      );
    }
    const cost = kind === 'neigong' ? neigongCost(lv) : skillCost(lv);
    const short = Math.ceil(cost - pr.cultivation);
    const can = !maxed && !t && short <= 0;
    const b = h(
      'button',
      {
        type: 'button',
        class: 'btn small',
        disabled: !can,
        title: t ? `กำลังฝึก${this.world.trainingName(t)}อยู่ ฝึกได้ทีละอย่าง` : '',
      },
      maxed ? 'ขั้นสูงสุด' : `ฝึก · ${fmt(cost)} · ${fmtDuration(trainSeconds(kind, lv))}`,
    );
    b.addEventListener('click', () =>
      this.command(kind === 'neigong' ? { t: 'trainNeigong', id } : { t: 'trainSkill', id }),
    );
    // Short of points: how many more, under the button.
    if (maxed || t || short <= 0) return b;
    return h('span', { class: 'train-cta' }, b, h('small', { class: 'short' }, `ขาดอีก ${fmt(short)} แต้ม`));
  }

  /** Character sheet: who they are, what they own, their attributes and stats in groups, titles. */
  private charTab(): HTMLElement {
    const w = this.world;
    const pr = w.progress;
    const p = w.player;
    const rank = rankFor(pr.cultivationSpent);
    const attr = attributesFor(pr.activeNeigong, pr.neigong[pr.activeNeigong] ?? 1);
    const d = deriveStats(attr);
    const row = (k: string, v: string) => h('div', { class: 'kv' }, h('span', {}, k), h('b', {}, v));
    const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
    const coin = (k: string, v: number, icon?: 'silver' | 'jade' | 'cultivation') =>
      h('div', { class: 'coin' }, h('small', {}, icon ? moneyIcon(icon) : null, k), h('b', {}, fmt(v)));
    return h(
      'div',
      { class: 'tab-char' },
      h(
        'section',
        { class: 'char-card' },
        h('div', { class: 'doll-fig' }, this.figure()),
        h(
          'div',
          { class: 'hero-info' },
          this.wornTitle(),
          h('div', { class: 'pname' }, pr.name),
          h(
            'div',
            { class: 'hero-tags' },
            h('span', { class: 'chip on' }, rank.name),
            h('span', { class: 'chip' }, pr.school ? `สำนัก${getSchool(pr.school).name}` : 'ไร้สำนัก'),
            h('span', { class: 'chip' }, getArt(pr.activeArt).name),
            h('span', { class: 'chip' }, `ฝ่าย${KARMA_LABELS[pr.karma]}`),
          ),
          h(
            'div',
            { class: 'rankbar' },
            fillBar(
              rank.next
                ? (pr.cultivationSpent - rankThreshold(rank.index)) / (rank.next - rankThreshold(rank.index))
                : 1,
            ),
            h(
              'small',
              {},
              rank.next
                ? `ใช้แต้มฝึกปรือไปแล้ว ${fmt(pr.cultivationSpent)} / ${fmt(rank.next)} เพื่อขึ้นระดับถัดไป`
                : 'บรรลุระดับสูงสุด',
            ),
          ),
          h(
            'div',
            { class: 'wallet' },
            coin('แต้มฝึกปรือ', pr.cultivation, 'cultivation'),
            coin('ตำลึง', pr.gold, 'silver'),
            coin('หยก', pr.jade, 'jade'),
            coin('ค่า PK', pr.pkPoints),
          ),
        ),
      ),
      h(
        'section',
        { class: 'attr-row' },
        ...(Object.keys(ATTRIBUTE_LABELS) as (keyof Attributes)[]).map((k) =>
          h(
            'div',
            { class: 'attr', title: ATTRIBUTE_EFFECTS[k] },
            h('small', {}, ATTRIBUTE_LABELS[k]),
            h('b', {}, String(attr[k])),
          ),
        ),
      ),
      h(
        'p',
        { class: 'note' },
        `คุณสมบัติมาจาก${NEIGONG[pr.activeNeigong]!.name} ขั้น ${pr.neigong[pr.activeNeigong]} · ชี้ที่ช่องเพื่อดูผล`,
      ),
      h(
        'div',
        { class: 'stat-groups' },
        h(
          'section',
          {},
          h('h4', {}, 'ชีวิต'),
          row('พลังชีวิตสูงสุด', String(p.maxHp)),
          row('ลมปราณสูงสุด', String(p.maxQi)),
          row('ฟื้นฟูพลังชีวิต', `${p.hpRegen}/วินาที`),
          row('ฟื้นฟูลมปราณ', `${p.qiRegen}/วินาที`),
          row('ความโกรธสูงสุด', String(RAGE.max)),
        ),
        h(
          'section',
          {},
          h('h4', {}, 'โจมตี'),
          row('พลังระยะประชิด', String(p.atk)),
          row('พลังโจมตีภายใน', String(p.magic)),
          row('พลังระยะไกล', String(d.shotPower)),
          row('แม่นยำ นอก / ใน', `${p.hit} / ${p.magicHit}`),
          row('คริติคอล นอก / ใน', `${pct(p.crit)} / ${pct(p.magicCrit)}`),
        ),
        h(
          'section',
          {},
          h('h4', {}, 'ป้องกัน'),
          row('ป้องกัน นอก / ใน', `${p.def} / ${p.magicDef}`),
          row('หลบหลีก', String(p.dodge)),
          row('ความแข็งแกร่ง', String(p.toughness)),
          row('วิชาที่เรียนแล้ว', `${pr.arts.length} ชุด`),
        ),
      ),
      this.titlesSection(),
    );
  }

  /** The title over the character card: the worn title in its plate, any other as text. */
  private wornTitle(): HTMLElement {
    const worn = TITLES[this.world.progress.titleWorn];
    const title = this.world.player.title;
    return worn && worn.name === title
      ? h('div', { class: 'ptitle has-plate' }, plateEl(plateOf(worn), title, 1.5))
      : h('div', { class: 'ptitle' }, title);
  }

  /** Every title: earned ones can be shown over the head, the rest say how to earn them. */
  private titlesSection(): HTMLElement {
    const pr = this.world.progress;
    return h(
      'section',
      { class: 'titles' },
      h('h4', {}, `ฉายา · ได้รับแล้ว ${pr.titles.length}/${TITLE_LIST.length}`),
      h(
        'div',
        { class: 'titles-grid' },
        ...TITLE_LIST.map((t) => {
          const owned = pr.titles.includes(t.id);
          const worn = pr.titleWorn === t.id;
          const b = h(
            'button',
            { type: 'button', class: `btn small ${worn ? '' : 'ghost'}`, disabled: !owned || worn },
            worn ? 'กำลังใช้' : owned ? 'ใช้ฉายานี้' : 'ยังไม่ได้รับ',
          );
          b.addEventListener('click', () => this.command({ t: 'setTitle', id: t.id }));
          return h(
            'div',
            { class: `title-row ${owned ? '' : 'locked'}` },
            iconEl(titleIcon(t.id), 'ico32 title-ico'),
            h('b', { class: 'title-name has-plate' }, plateEl(plateOf(t), t.name)),
            h('small', {}, t.how),
            b,
          );
        }),
      ),
    );
  }

  /** The player's figure dressed in a fashion outfit, drawn large. */
  private outfitPreview(look: string): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = 96;
    c.height = 96;
    c.className = 'skin-pic';
    const set = this.sprites?.of({ sprite: this.world.player.sprite, outfit: [look] });
    const img = set?.idle[0];
    if (set && img) {
      const g = c.getContext('2d')!;
      g.imageSmoothingEnabled = false;
      const k = Math.min(96 / artH(img), 2);
      g.drawImage(
        img,
        48 + (artX(img) - set.ax) * k,
        94 + (artY(img) - set.ay) * k,
        artW(img) * k,
        artH(img) * k,
      );
    }
    return c;
  }

  /** The item mall: looks for jade, each with a picture, its price and whether it is owned. */
  private mallTab(): HTMLElement {
    const pr = this.world.progress;
    const card = (e: (typeof MALL)[number]) => {
      const skin = e.kind === 'skin' ? getSkin(e.id) : null;
      const mount = e.kind === 'mount' ? getMount(e.id) : null;
      const owned = (e.kind === 'skin' ? pr.skins : pr.mounts).includes(e.id);
      const pic = skin
        ? skin.look
          ? this.outfitPreview(skin.look)
          : skinPreview(skin.color)
        : iconEl(mountIcon(e.id), 'ico96 skin-pic');
      const b = h(
        'button',
        { type: 'button', class: `btn ${owned ? 'ghost' : ''}`, disabled: owned || pr.jade < e.price },
        owned ? 'มีแล้ว' : `ซื้อ ${fmt(e.price)} หยก`,
      );
      b.addEventListener('click', () => this.command({ t: 'mallBuy', kind: e.kind, id: e.id }));
      return h(
        'section',
        { class: `skin-card mall-card ${owned ? 'on' : ''}` },
        pic,
        h(
          'div',
          {},
          e.tag ? h('span', { class: 'mall-tag' }, e.tag) : null,
          h('div', { class: 'art-name' }, skin ? skin.name : mount!.name),
          h(
            'div',
            { class: 'art-school' },
            skin
              ? SKIN_SLOT_LABELS[skin.slot]
              : `พาหนะ · เร็วขึ้น ${Math.round((mount!.speedMul - 1) * 100)}%`,
          ),
          h('p', { class: 'note' }, skin ? skin.desc : mount!.desc),
          b,
        ),
      );
    };
    return h(
      'div',
      { class: 'tab-skin' },
      h(
        'p',
        { class: 'lede' },
        `หยกเซียนของเจ้า ${fmt(pr.jade)} · ได้จากการเติมหยก ส่งภารกิจ และปราบจอมมารพันกระบี่ · ของในร้านเปลี่ยนแค่รูปลักษณ์ ไม่เพิ่มพลัง`,
      ),
      ...(this.checkout ? this.topUpSection() : []),
      h('h4', {}, 'สินค้า'),
      ...MALL.map(card),
    );
  }

  /**
   * ประลองยุทธ: today's rank and challenges, the build that fights, five to challenge, the last
   * bout's account (its lines appear one after another), the ranking and the day's rewards.
   */
  private arenaTab(): HTMLElement {
    const w = this.world;
    const pr = w.progress;
    const a = pr.arena;
    const me = w.arenaRank();
    const power = (b: ArenaBuild) => fmt(arenaPower(arenaStats(b, b === mine ? pr.rolls : {})));
    const mine = w.arenaBuild(me);
    const left = ARENA.attemptsPerDay - a.attempts;
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    const card = (rank: number) => {
      const b = w.arenaBuild(rank);
      const fight = h(
        'button',
        { type: 'button', class: 'btn small', disabled: left <= 0 || !!w.duel },
        'ท้าประลอง',
      );
      // Close the menu to watch the bout in the ring.
      fight.addEventListener('click', () => {
        this.close();
        this.command({ t: 'arenaFight', rank });
      });
      return h(
        'div',
        { class: 'arena-pick' },
        h('b', { class: 'arena-rank' }, String(rank)),
        h(
          'div',
          {},
          h('div', { class: 'art-name' }, b.name),
          h(
            'div',
            { class: 'art-school' },
            `${b.title} · ${getArt(b.art).name} · ${getNeigong(b.neigong).name} ขั้น ${b.neigongLevel}`,
          ),
        ),
        h('span', { class: 'arena-power' }, `พลัง ${power(b)}`),
        fight,
      );
    };
    const refresh = h('button', { type: 'button', class: 'btn small ghost' }, 'สุ่มใหม่');
    refresh.addEventListener('click', () => this.command({ t: 'arenaRefresh' }));
    const last = w.arenaLast;
    const report = last
      ? h(
          'section',
          { class: 'arena-report' },
          h(
            'div',
            { class: `arena-result ${last.won ? 'won' : 'lost'}` },
            `ครั้งล่าสุด: ${last.won ? 'ชนะ' : 'แพ้'} ${last.foe}`,
            last.to !== last.from ? ` · อันดับ ${last.from} → ${last.to}` : '',
          ),
        )
      : null;
    const order = a.order;
    const row = (rank: number) => {
      const b = w.arenaBuild(rank);
      return h(
        'div',
        { class: `ladder-rank ${order[rank - 1] === -1 ? 'me' : ''}` },
        h('b', {}, String(rank)),
        h('span', {}, b.name, h('small', {}, ` ${b.title}`)),
        h('span', {}, power(b)),
      );
    };
    return h(
      'div',
      { class: 'tab-arena' },
      h(
        'p',
        { class: 'lede' },
        'ลานที่ทุกคนเจอกันได้ ตัวละครสองฝ่ายเข้าลานแล้วสู้กันเองอัตโนมัติด้วยวิชา กำลังภายใน และอาวุธ/ชุดที่ตั้งไว้ ใครล้มก่อนแพ้ · ชนะคนอันดับสูงกว่าได้อันดับนั้นไป · ปิดวันรับรางวัลตามอันดับแล้วเริ่มใหม่',
      ),
      h(
        'div',
        { class: 'ladder-head' },
        h('div', {}, h('small', {}, 'อันดับวันนี้'), h('b', {}, `${me} / ${order.length}`)),
        h('div', {}, h('small', {}, 'ท้าได้อีก'), h('b', {}, `${left} / ${ARENA.attemptsPerDay}`)),
        h('div', {}, h('small', {}, 'ชนะ / แพ้'), h('b', {}, `${a.wins} / ${a.losses}`)),
        h(
          'div',
          {},
          h('small', {}, 'รีเซ็ตใน'),
          h('b', {}, fmtDuration((midnight.getTime() - Date.now()) / 1000)),
        ),
        h('div', {}, h('small', {}, 'รางวัลถ้าจบตอนนี้'), h('b', {}, arenaReward(me).label)),
      ),
      h(
        'p',
        { class: 'note' },
        `ชุดที่ใช้สู้: ${getArt(mine.art).name} (ขั้นเฉลี่ย ${mine.skillLevel}) · ${getNeigong(mine.neigong).name} ขั้น ${mine.neigongLevel} · ${
          Object.values(mine.equipment)
            .filter((id): id is string => !!id)
            .map((id) => itemOf(pr.rolls, id)?.name ?? id)
            .join(' · ') || 'ไม่สวมอุปกรณ์'
        } · พลัง ${power(mine)} · เปลี่ยนได้ที่เมนูวิชา กำลังภายใน และย่าม`,
      ),
      h('h4', {}, 'เลือกคู่ประลอง ', refresh),
      ...w.arenaPicks.map(card),
      report,
      h('h4', {}, 'อันดับ'),
      ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(row),
      me > 10 ? row(me) : null,
      h('h4', {}, 'รางวัลปิดวัน (ต้องท้าประลองอย่างน้อย 1 ครั้ง)'),
      ...ARENA_REWARDS.map((r) =>
        h(
          'div',
          { class: `topup-mile ${arenaReward(me) === r ? 'on' : ''}` },
          h('b', {}, r.label),
          h('span', {}, rewardLabel(r.reward)),
          h('span', {}),
        ),
      ),
    );
  }

  /** The ladders: standing, queue button, the ranking, tier rewards and the points shop. */
  private ladderTab(): HTMLElement {
    const w = this.world;
    const pr = w.progress;
    const id = this.ladderView ?? ladderFor(pr.karma);
    const def = LADDERS[id];
    const st = pr.ladder[id];
    const tier = tierAt(def, st.rating);
    const switcher = h(
      'div',
      { class: 'ladder-switch', role: 'tablist' },
      ...LADDER_IDS.map((l) => {
        const b = h(
          'button',
          {
            type: 'button',
            role: 'tab',
            class: `btn small ${l === id ? '' : 'ghost'}`,
            ariaSelected: String(l === id),
          },
          `${LADDERS[l].glyph} ${LADDERS[l].name}`,
        );
        b.addEventListener('click', () => (this.ladderView = l));
        return b;
      }),
    );
    const d = w.duel;
    const allowed = def.allows(pr.karma);
    const join = h(
      'button',
      {
        type: 'button',
        class: `btn ${d?.phase === 'queue' ? 'ghost' : ''}`,
        disabled: !allowed || (!!d && d.phase !== 'queue'),
      },
      d?.phase === 'queue' ? 'กำลังจับคู่… ยกเลิก' : d ? 'กำลังประลอง' : 'เข้าร่วมประลอง',
    );
    join.addEventListener('click', () =>
      this.command(d?.phase === 'queue' ? { t: 'ladderCancel' } : { t: 'ladderJoin', ladder: id }),
    );
    // The ranking: the contenders and the player, by rating.
    const me = { name: pr.name, title: 'เจ้า', rating: st.rating, me: true };
    const board = [...def.contenders.map((c) => ({ ...c, me: false })), me].sort(
      (a, b) => b.rating - a.rating,
    );
    const myRank = board.indexOf(me) + 1;
    const row = (e: (typeof board)[number], i: number) =>
      h(
        'div',
        { class: `ladder-rank ${e.me ? 'me' : ''}` },
        h('b', {}, String(i + 1)),
        h('span', {}, e.name, h('small', {}, ` ${e.title}`)),
        h('span', {}, fmt(e.rating)),
      );
    const tiers = def.tiers.map((t, i) => {
      const taken = st.claimed.includes(i);
      const b = h(
        'button',
        {
          type: 'button',
          class: `btn small ${taken ? 'ghost' : ''}`,
          disabled: taken || st.rating < t.rating,
        },
        taken ? 'รับแล้ว' : st.rating >= t.rating ? 'รับรางวัล' : `${fmt(t.rating)}`,
      );
      b.addEventListener('click', () => this.command({ t: 'ladderClaim', ladder: id, tier: i }));
      return h(
        'div',
        { class: `topup-mile ${st.rating >= t.rating ? 'on' : ''}` },
        h('b', {}, t.name),
        h('span', {}, rewardLabel(t.reward)),
        b,
      );
    });
    const wares = def.shop.map((x, i) => {
      const b = h(
        'button',
        { type: 'button', class: 'btn small', disabled: st.points < x.price },
        `แลก ${fmt(x.price)}`,
      );
      b.addEventListener('click', () => this.command({ t: 'ladderBuy', ladder: id, index: i }));
      const item = x.kind === 'item' ? ITEMS[x.id]! : null;
      return h(
        'div',
        { class: 'topup-mile' },
        h(
          'b',
          {},
          item ? iconEl(itemIcon(item), 'ico32') : null,
          x.kind === 'item' ? ITEMS[x.id]!.name : x.name,
        ),
        h(
          'span',
          {},
          item ? `${RARITY_LABELS[item.rarity]} · ${SLOT_LABELS[item.slot]} · ${statLine(item.stats)}` : '',
        ),
        b,
      );
    });
    return h(
      'div',
      { class: 'tab-ladder' },
      switcher,
      h(
        'p',
        { class: 'lede' },
        `${def.name}สำหรับ${def.side} · จับคู่กับผู้ที่คะแนนใกล้กัน ชนะได้คะแนน แพ้เสียคะแนน · ทุกรอบได้${def.points} (ชนะ 30 แพ้ 10)`,
      ),
      h(
        'div',
        { class: 'ladder-head' },
        h('div', {}, h('small', {}, 'คะแนน'), h('b', {}, fmt(st.rating))),
        h('div', {}, h('small', {}, 'ขั้น'), h('b', {}, tier >= 0 ? def.tiers[tier]!.name : 'ยังไม่มีขั้น')),
        h('div', {}, h('small', {}, 'อันดับ'), h('b', {}, `${myRank} / ${board.length}`)),
        h('div', {}, h('small', {}, 'ชนะ / แพ้'), h('b', {}, `${st.wins} / ${st.losses}`)),
        h('div', {}, h('small', {}, def.points), h('b', {}, fmt(st.points))),
      ),
      allowed ? null : h('p', { class: 'note' }, `เข้าร่วมได้เฉพาะ${def.side}`),
      h('div', { class: 'dlg-actions pass-actions' }, join),
      h('h4', {}, 'อันดับ'),
      ...board.slice(0, 10).map(row),
      myRank > 10 ? row(me, myRank - 1) : null,
      h('h4', {}, 'รางวัลตามขั้น (รับได้ครั้งเดียวต่อฤดูกาล)'),
      ...tiers,
      h('h4', {}, `ร้าน${def.name} · ${def.points} ${fmt(st.points)}`),
      ...wares,
    );
  }

  /** The season's battle pass: XP bar, premium unlock, and every tier's two rewards. */
  private passTab(): HTMLElement {
    const p = this.world.progress.pass;
    const level = passLevel(p.xp);
    const maxed = level >= SEASON.maxLevel;
    const daysLeft = Math.max(
      0,
      Math.ceil((Date.parse(`${SEASON.ends}T23:59:59+07:00`) - Date.now()) / 864e5),
    );
    const claimable = PASS_TIERS.some(
      (t) =>
        t.level <= level &&
        (!p.claimed.free.includes(t.level) || (p.premium && !p.claimed.premium.includes(t.level))),
    );
    const buy = h(
      'button',
      { type: 'button', class: 'btn', disabled: p.premium || this.world.progress.jade < SEASON.premiumPrice },
      p.premium ? 'พรีเมียมแล้ว' : `ปลดล็อกพรีเมียม ${fmt(SEASON.premiumPrice)} หยก`,
    );
    buy.addEventListener('click', () => this.command({ t: 'passBuy' }));
    const all = h('button', { type: 'button', class: 'btn ghost', disabled: !claimable }, 'รับทั้งหมด');
    all.addEventListener('click', () => this.command({ t: 'passClaimAll' }));
    const cell = (level: number, track: PassTrack, r: PassReward) => {
      const taken = p.claimed[track].includes(level);
      const locked = track === 'premium' && !p.premium;
      const reached = level <= level;
      const b = h(
        'button',
        { type: 'button', class: 'btn small', disabled: taken || locked || !reached },
        taken ? 'รับแล้ว' : locked ? 'ล็อก' : 'รับ',
      );
      b.addEventListener('click', () => this.command({ t: 'passClaim', level, track }));
      return h(
        'div',
        { class: `pass-cell ${track} ${taken ? 'taken' : ''}` },
        h('span', {}, rewardLabel(r)),
        reached ? b : null,
      );
    };
    return h(
      'div',
      { class: 'tab-pass' },
      h('h4', {}, SEASON.name),
      h(
        'p',
        { class: 'lede' },
        `ถึง ${new Date(SEASON.ends).toLocaleDateString('th-TH', { dateStyle: 'long' })} (อีก ${daysLeft} วัน) · ได้แต้มจากการปราบศัตรูและส่งภารกิจ · ฝั่งพรีเมียมให้ชุด ฉายา และหยกคืน ไม่เพิ่มพลัง`,
      ),
      h(
        'div',
        { class: 'pass-head' },
        h('b', {}, `ขั้น ${level}`),
        fillBar(maxed ? 1 : (p.xp % SEASON.xpPerLevel) / SEASON.xpPerLevel),
        h('span', {}, maxed ? 'ขั้นสูงสุด' : `${p.xp % SEASON.xpPerLevel} / ${SEASON.xpPerLevel}`),
      ),
      h('div', { class: 'dlg-actions pass-actions' }, all, buy),
      h(
        'div',
        { class: 'pass-row pass-labels' },
        h('span', {}, 'ขั้น'),
        h('span', {}, 'ฟรี'),
        h('span', {}, 'พรีเมียม'),
      ),
      ...PASS_TIERS.map((t) =>
        h(
          'div',
          { class: `pass-row ${t.level <= level ? 'on' : ''}` },
          h('b', {}, String(t.level)),
          cell(t.level, 'free', t.free),
          cell(t.level, 'premium', t.premium),
        ),
      ),
    );
  }

  /** Jade packs for baht (each doubled the first time) and the rewards for the running total. */
  private topUpSection(): HTMLElement[] {
    const t = this.world.progress.topUp;
    const packs = TOPUP_PACKS.map((k) => {
      const first = !t.packs.includes(k.id);
      const b = h(
        'button',
        { type: 'button', class: `topup-pack ${first ? 'first' : ''}` },
        k.tag ? h('span', { class: 'mall-tag' }, k.tag) : null,
        h('b', {}, `${fmt(topUpJade(k, first))} หยก`),
        h('small', {}, first ? `ครั้งแรก x2` : k.bonus ? `รวมโบนัส ${fmt(k.bonus)}` : 'หยกเซียน'),
        h('span', { class: 'topup-baht' }, `${fmt(k.baht)} บาท`),
      );
      b.addEventListener('click', () => this.checkout?.open(k, first));
      return b;
    });
    const milestones = TOPUP_MILESTONES.map((m) => {
      const claimed = t.claimed.includes(m.baht);
      const reached = t.baht >= m.baht;
      const b = h(
        'button',
        { type: 'button', class: `btn small ${claimed ? 'ghost' : ''}`, disabled: claimed || !reached },
        claimed ? 'รับแล้ว' : reached ? 'รับรางวัล' : `อีก ${fmt(m.baht - t.baht)} บาท`,
      );
      b.addEventListener('click', () => this.command({ t: 'topUpClaim', baht: m.baht }));
      return h(
        'div',
        { class: `topup-mile ${reached ? 'on' : ''}` },
        h('b', {}, `${fmt(m.baht)} บาท`),
        h('span', {}, rewardLabel(m)),
        b,
      );
    });
    return [
      h('h4', {}, 'เติมหยกเซียน'),
      h('div', { class: 'topup-grid' }, ...packs),
      h('h4', {}, `รางวัลยอดเติมสะสม · เติมแล้ว ${fmt(t.baht)} บาท`),
      ...milestones,
    ];
  }

  /** The merchant's wares to buy, and the bag's contents to sell. */
  private shopTab(): HTMLElement {
    const pr = this.world.progress;
    const buy = h(
      'div',
      { class: 'items' },
      ...SHOP.map((e) => {
        const it = ITEMS[e.item]!;
        const b = h(
          'button',
          { type: 'button', class: 'btn small', disabled: pr.gold < e.price },
          `ซื้อ ${fmt(e.price)} ตำลึง`,
        );
        b.addEventListener('click', () => this.command({ t: 'buy', item: e.item }));
        return h(
          'section',
          { class: `item r-${it.rarity}` },
          h(
            'div',
            { class: 'item-head' },
            iconEl(itemIcon(it), 'ico32'),
            h(
              'div',
              {},
              h('div', { class: 'item-name' }, it.name),
              h('div', { class: 'item-meta' }, `${SLOT_LABELS[it.slot]} · ${RARITY_LABELS[it.rarity]}`),
            ),
            b,
          ),
          h('div', { class: 'item-stats' }, statLine(it.stats)),
        );
      }),
    );
    const sell = h(
      'div',
      { class: 'items' },
      ...[...new Set(pr.inventory)].map((id) => {
        const it = itemOf(pr.rolls, id)!;
        const worn = Object.values(pr.equipment).includes(id);
        const price = sellPrice(pr.rolls[id]?.base ?? id, !!pr.rolls[id]?.art);
        const b = h(
          'button',
          { type: 'button', class: 'btn small ghost', disabled: worn },
          worn ? 'สวมอยู่' : `ขาย ${fmt(price)}`,
        );
        b.addEventListener('click', () => this.command({ t: 'sell', id }));
        return h(
          'section',
          { class: `item r-${it.rarity}` },
          h(
            'div',
            { class: 'item-head' },
            iconEl(itemIcon(it), 'ico32'),
            h('div', { class: 'item-name' }, itemName(it)),
            b,
          ),
          h('div', { class: 'item-stats' }, statLine(fullStats(it), it.art)),
        );
      }),
    );
    return h(
      'div',
      { class: 'tab-bag' },
      h('p', { class: 'lede' }, `เถ้าแก่ฉาง · พ่อค้าเร่ · ตำลึงของเจ้า ${fmt(pr.gold)}`),
      h('h3', { class: 'group' }, 'ซื้อ'),
      buy,
      h('h3', { class: 'group' }, 'ขาย'),
      sell,
    );
  }

  /** Mounts owned: a picture, what each does, and a button to choose the one X summons. */
  private mountTab(): HTMLElement {
    const pr = this.world.progress;
    const riding = this.world.player.mount;
    return h(
      'div',
      { class: 'tab-skin' },
      h('p', { class: 'lede' }, 'เลือกพาหนะแล้วกด X เพื่อขึ้น-ลง ขี่ได้เมื่อไม่อยู่ในการต่อสู้'),
      ...pr.mounts.map((id) => {
        const m = getMount(id);
        const picked = pr.mountPick === id;
        const b = h(
          'button',
          { type: 'button', class: `btn ${picked ? 'ghost' : ''}`, disabled: picked },
          picked ? 'เลือกอยู่' : 'เลือกพาหนะนี้',
        );
        b.addEventListener('click', () => this.command({ t: 'setMount', id }));
        const ride = h(
          'button',
          { type: 'button', class: 'btn ghost' },
          riding === id ? 'ลงจากพาหนะ' : 'ขี่เลย',
        );
        ride.addEventListener('click', () => {
          if (!picked) this.command({ t: 'setMount', id });
          this.command({ t: 'mount' });
        });
        return h(
          'section',
          { class: `skin-card ${picked ? 'on' : ''}` },
          iconEl(mountIcon(id), 'ico96 skin-pic'),
          h(
            'div',
            {},
            h('div', { class: 'art-name' }, m.name),
            h(
              'div',
              { class: 'art-school' },
              `เร็วขึ้น ${Math.round((m.speedMul - 1) * 100)}%${m.fly ? ' · บินได้ (Space ขึ้น · Q ลง)' : ''}`,
            ),
            h('p', { class: 'note' }, m.desc),
            h('div', { class: 'ng-actions' }, b, ride),
          ),
        );
      }),
    );
  }

  /** The fitting room: every skin and look-changing piece of gear, owned or not, to try on. */
  private skinTab(): HTMLElement {
    return h(
      'div',
      { class: 'tab-skin' },
      h(
        'p',
        { class: 'lede' },
        'ห้องลองชุด · กดเพื่อลองสวมได้ทุกชุด แม้ยังไม่มี สกินเปลี่ยนแค่รูปลักษณ์ ไม่มีผลต่อการต่อสู้',
      ),
      fittingRoom({
        world: this.world,
        sprites: this.sprites,
        fit: this.fit,
        command: this.command,
        openMall: () => this.open('mall'),
      }),
    );
  }

  /** Martial arts: every art in a list, the picked one with its moves and training on the right. */
  private artsTab(): HTMLElement {
    const pr = this.world.progress;
    // Jianghu (wandering) arts first, then school arts grouped by school: one group per school,
    // in the order the schools first appear (later art lists add to a school's group, not a new one).
    const all = Object.values(MARTIAL_ARTS).filter((a) => !a.hidden);
    const schools = [...new Set(all.flatMap((a) => (a.schoolId ? [a.schoolId] : [])))];
    const arts = [
      ...all.filter((a) => !a.schoolId),
      ...schools.flatMap((id) => all.filter((a) => a.schoolId === id)),
    ];
    const pickId = MARTIAL_ARTS[this.pick.arts ?? '']?.hidden === false || (MARTIAL_ARTS[this.pick.arts ?? ''] && !MARTIAL_ARTS[this.pick.arts ?? '']!.hidden) ? this.pick.arts! : pr.activeArt;
    const list = h('nav', { class: 'split-list', ariaLabel: 'รายการวิชา' });
    let artGroup = '';
    for (const art of arts) {
      const g = art.schoolId ? `สำนัก${getSchool(art.schoolId).name}` : 'ยุทธจักร (ไร้สำนัก)';
      if (g !== artGroup) {
        artGroup = g;
        list.append(h('h3', { class: 'group' }, g));
      }
      const learned = pr.arts.includes(art.id);
      const active = pr.activeArt === art.id;
      const b = h(
        'button',
        {
          type: 'button',
          class: `li ${art.id === pickId ? 'sel' : ''} ${learned ? '' : 'locked'}`,
          ariaPressed: String(art.id === pickId),
        },
        rowIcon(artIcon(art.id)),
        h('span', { class: 'li-name' }, art.name),
        h(
          'small',
          { class: `li-state ${active ? 'on' : ''}` },
          active ? 'ใช้อยู่' : learned ? 'เรียนแล้ว' : 'ยังไม่เรียน',
        ),
      );
      b.addEventListener('click', () => this.select('arts', art.id));
      list.append(b);
    }

    const art = MARTIAL_ARTS[pickId]!;
    const learned = pr.arts.includes(art.id);
    let action: HTMLElement;
    if (pr.activeArt === art.id) action = h('span', { class: 'chip on' }, 'กำลังใช้');
    else if (learned) {
      action = h('button', { type: 'button', class: 'btn small' }, 'ใช้วิชานี้');
      action.addEventListener('click', () => this.command({ t: 'setArt', id: art.id }));
    } else if (!art.schoolId && art.learnCost !== undefined) {
      // Wandering arts are learned right here.
      const cost = art.learnCost;
      action = h(
        'button',
        { type: 'button', class: 'btn small', disabled: pr.cultivation < cost },
        `เรียน · ${fmt(cost)}`,
      );
      action.addEventListener('click', () => this.command({ t: 'learnArt', id: art.id }));
    } else
      action = h(
        'span',
        { class: 'chip' },
        art.schoolId ? `เรียนที่สำนัก${getSchool(art.schoolId).name}` : 'ยังไม่ได้เรียน',
      );
    const detail = h(
      'section',
      { class: `split-detail ${learned ? '' : 'locked'}` },
      h(
        'div',
        { class: 'art-head' },
        headIcon(artIcon(art.id)),
        h(
          'div',
          {},
          h('div', { class: 'art-name' }, art.name),
          h(
            'div',
            { class: 'art-school' },
            `${art.school} · ${art.weapon === 'sword' ? 'กระบี่' : 'มือเปล่า'} · สาย${DAMAGE_TYPE_LABELS[art.damage]}`,
          ),
        ),
        elementBadge(art.element),
        action,
      ),
      h('p', { class: 'note' }, art.desc),
      h('h4', {}, `กระบวนท่า ${art.skills.length} ท่า`),
    );
    art.skills.forEach((id, i) => {
      const s = getSkill(id);
      const lv = pr.skillLevels[id] ?? 1;
      const maxed = lv >= MAX_SKILL_LEVEL;
      detail.append(
        h(
          'div',
          { class: 'skill-row' },
          h('span', { class: `glyph k-${s.kind}` }, iconEl(skillIcon(s))),
          h(
            'div',
            { class: 'skill-info' },
            h(
              'div',
              {},
              h('b', {}, s.name),
              h('span', { class: `kind k-${s.kind}` }, `${KIND_LABEL[s.kind]}${s.lockForm ? ' · ท่าล็อกเป้า' : ''}`),
              h('span', { class: 'lv' }, `[${i + 1}] ขั้น ${lv}/${MAX_SKILL_LEVEL}`),
            ),
            h(
              'small',
              {},
              s.desc,
              s.power ? ` · พลัง ×${(s.power * skillPowerMult(lv)).toFixed(2)}` : '',
              ` · ${s.rage ? `ความโกรธ ${s.rage}` : `ลมปราณ ${s.qi}`} · คูลดาวน์ ${s.cooldown} วินาที`,
            ),
            learned ? fillBar(lv / MAX_SKILL_LEVEL) : null,
          ),
          learned ? this.trainButton('skill', id, lv, maxed) : null,
        ),
      );
    });
    return h(
      'div',
      { class: 'tab-arts' },
      this.tabBar('ไม่มีอาชีพตายตัว เรียนได้ทุกวิชา เลือกใช้ได้ทีละชุด'),
      h('div', { class: 'split' }, list, detail),
    );
  }

  /** Neigong: every scripture in a list, the picked one with its gains and milestones on the right. */
  private neigongTab(): HTMLElement {
    const pr = this.world.progress;
    // Jianghu scriptures first, then each school's own.
    const ordered = (['jianghu', 'school'] as const).flatMap((c) =>
      // A new character sees what is learned and what is near (the first tiers of the jianghu scriptures); the rest
      // of the list (other schools') shows once it is learned or its school is joined.
      Object.values(NEIGONG).filter(
        (n) => n.category === c && (pr.neigong[n.id] != null || n.id === pr.activeNeigong || n.id === this.pick.neigong || (c === 'jianghu' && n.tier <= 2)),
      ),
    );
    const pickId = NEIGONG[this.pick.neigong ?? ''] ? this.pick.neigong! : pr.activeNeigong;
    const list = h('nav', { class: 'split-list', ariaLabel: 'รายการกำลังภายใน' });
    let group = '';
    for (const n of ordered) {
      if (n.category !== group) {
        group = n.category;
        list.append(h('h3', { class: 'group' }, NEIGONG_CATEGORY_LABELS[n.category]));
      }
      const lv = pr.neigong[n.id];
      const active = pr.activeNeigong === n.id;
      const b = h(
        'button',
        {
          type: 'button',
          class: `li ${n.id === pickId ? 'sel' : ''} ${lv != null ? '' : 'locked'}`,
          ariaPressed: String(n.id === pickId),
        },
        rowIcon(neigongIcon(n.id)),
        h('span', { class: 'li-name' }, n.name),
        h(
          'small',
          { class: `li-state ${active ? 'on' : ''}` },
          active ? 'ใช้อยู่' : lv != null ? `${lv}/${n.maxLevel}` : `ขั้น ${n.tier}`,
        ),
      );
      b.addEventListener('click', () => this.select('neigong', n.id));
      list.append(b);
    }

    const n = NEIGONG[pickId]!;
    const lv = pr.neigong[n.id];
    const learned = lv != null;
    const actions = h('div', { class: 'ng-actions' });
    if (pr.activeNeigong === n.id) actions.append(h('span', { class: 'chip on' }, 'กำลังใช้'));
    else if (learned) {
      const b = h('button', { type: 'button', class: 'btn small ghost' }, 'เดินลมปราณนี้');
      b.addEventListener('click', () => this.command({ t: 'setNeigong', id: n.id }));
      actions.append(b);
    }
    if (learned) actions.append(this.trainButton('neigong', n.id, lv, lv >= n.maxLevel));
    else if (n.learnCost !== undefined) {
      // Higher scriptures are learned right here once the lower one is mastered.
      const why = neigongLearnBlock(pr.neigong, pr.school, n.id);
      if (why) actions.append(h('span', { class: 'chip' }, why));
      else {
        const b = h(
          'button',
          { type: 'button', class: 'btn small', disabled: pr.cultivation < n.learnCost },
          `เรียน · ${fmt(n.learnCost)}`,
        );
        b.addEventListener('click', () => this.command({ t: 'learnNeigong', id: n.id }));
        actions.append(b);
      }
    } else
      actions.append(
        h(
          'span',
          { class: 'chip' },
          n.schoolId ? `ได้เมื่อเข้าสำนัก${getSchool(n.schoolId).name}` : 'ยังไม่ได้เรียน',
        ),
      );
    const st = STATUSES[n.passive.status]!;
    const mile = (on: boolean, level: number, text: string, icon?: HTMLCanvasElement) =>
      h(
        'li',
        { class: on ? 'on' : '' },
        h('b', {}, `ขั้น ${level}`),
        h('span', {}, icon ?? null, text),
        h('small', {}, on ? 'ได้รับแล้ว' : 'ยังไม่ถึง'),
      );
    const detail = h(
      'section',
      { class: `split-detail ${learned ? '' : 'locked'}` },
      h(
        'div',
        { class: 'art-head' },
        headIcon(neigongIcon(n.id)),
        h(
          'div',
          {},
          h('div', { class: 'art-name' }, n.name),
          h(
            'div',
            { class: 'art-school' },
            `คัมภีร์ขั้น ${n.tier}${n.schoolId ? ` · สำนัก${getSchool(n.schoolId).name}` : ''}`,
          ),
        ),
        elementBadge(n.element),
      ),
      actions,
      h('p', { class: 'note' }, n.desc),
      h(
        'div',
        { class: 'ng-level' },
        h('span', {}, learned ? `ฝึกแล้ว ${lv}/${n.maxLevel}` : `ฝึกได้ ${n.maxLevel} ระดับ`),
        fillBar(learned ? lv / n.maxLevel : 0),
      ),
      h('h4', {}, 'ได้ต่อขั้น'),
      h(
        'div',
        { class: 'gain-chips' },
        ...(Object.entries(n.perLevel) as [keyof Attributes, number][]).map(([k, v]) =>
          h('span', { class: 'chip', title: ATTRIBUTE_EFFECTS[k] }, `${ATTRIBUTE_LABELS[k]} +${v}`),
        ),
      ),
      h('h4', {}, 'ขั้นสำคัญ'),
      h(
        'ol',
        { class: 'miles' },
        mile(
          learned && lv >= n.passive.level,
          n.passive.level,
          `${st.name} — ${st.desc.replace(/^.*?: /, '')}`,
          iconEl(statusIcon(n.passive.status), 'ico16 mile-ico'),
        ),
        mile(learned && lv >= n.maxLevel, n.maxLevel, `ฝึกจนตัน: ออร่า「${n.aura.name}」`),
      ),
    );
    return h(
      'div',
      { class: 'tab-ng' },
      this.tabBar('กำลังภายในเพิ่มคุณสมบัติตามขั้น ใช้ได้ทีละคัมภีร์'),
      h('div', { class: 'split' }, list, detail),
    );
  }

  /** School: before joining, every school side by side; after, its arts as a path to climb. */
  private schoolTab(): HTMLElement {
    const pr = this.world.progress;
    const wrap = h('div', { class: 'tab-school' });
    const moves = (artId: string) =>
      h(
        'div',
        { class: 'school-skills' },
        ...getArt(artId).skills.map((sid) => {
          const s = getSkill(sid);
          return h(
            'span',
            { class: `glyph k-${s.kind}`, title: `${s.name}: ${s.desc}` },
            iconEl(skillIcon(s)),
          );
        }),
      );
    if (!pr.school) {
      wrap.append(
        h(
          'p',
          { class: 'lede' },
          'เจ้ายังไม่มีสำนัก เดินเข้าวงวาร์ปข้างลานศิลาเก้ากระบี่ไปยังแมปของสำนักที่สนใจ แล้วพบผู้แทนสำนักกลางลาน (NPC ที่มีเครื่องหมาย ! สีฟ้า) เพื่อขอเข้าสำนัก',
        ),
      );
      wrap.append(
        h(
          'div',
          { class: 'school-grid' },
          ...Object.values(SCHOOLS).map((sc) =>
            h(
              'section',
              { class: 'school-card' },
              h(
                'div',
                { class: 'school-name' },
                iconEl(schoolIcon(sc.id), 'ico32'),
                h('div', { class: 'art-name' }, `สำนัก${sc.name}`),
              ),
              h('div', { class: 'art-school' }, sc.motto),
              h('p', { class: 'note' }, sc.desc),
              h('div', { class: 'kv' }, h('span', {}, 'ผู้แทน'), h('b', {}, sc.master.name)),
              h('div', { class: 'kv' }, h('span', {}, 'กำลังภายใน'), h('b', {}, NEIGONG[sc.neigong]!.name)),
              h('small', { class: 'path-line' }, sc.arts.map((a) => getArt(a).name).join(' → ')),
            ),
          ),
        ),
      );
      return wrap;
    }
    const sc = getSchool(pr.school);
    wrap.append(
      h(
        'section',
        { class: 'school-banner' },
        iconEl(schoolIcon(sc.id), 'ico64 school-crest'),
        h(
          'div',
          { class: 'ptitle' },
          // The highest badge's rank once one is claimed, the disciple's title before.
          badgeClaimed(pr) ? badgeTitleName(sc.id, badgeClaimed(pr)) : sc.discipleTitle,
        ),
        h('div', { class: 'pname' }, `สำนัก${sc.name}`),
        h('div', { class: 'prank' }, sc.motto),
        h('p', { class: 'note' }, sc.desc),
        h(
          'div',
          { class: 'hero-tags' },
          h('span', { class: 'chip' }, `กำลังภายใน: ${NEIGONG[sc.neigong]!.name}`),
          h('span', { class: 'chip' }, `แต้มฝึกปรือ ${fmt(pr.cultivation)}`),
        ),
      ),
      badgeSection(this.world, this.sprites, this.command) ?? '',
      h('h4', {}, 'เส้นทางวิชาของสำนัก'),
    );
    const path = h('ol', { class: 'school-path' });
    sc.arts.forEach((id, i) => {
      const art = getArt(id);
      const learned = pr.arts.includes(id);
      const reqOk = !art.requires || pr.arts.includes(art.requires);
      const cost = art.learnCost ?? 0;
      let action: HTMLElement;
      if (pr.activeArt === id) action = h('span', { class: 'chip on' }, 'กำลังใช้');
      else if (learned) {
        action = h('button', { type: 'button', class: 'btn small ghost' }, 'ใช้วิชานี้');
        action.addEventListener('click', () => this.command({ t: 'setArt', id }));
      } else {
        action = h(
          'button',
          { type: 'button', class: 'btn small', disabled: !reqOk || pr.cultivation < cost },
          `เรียน · ${fmt(cost)}`,
        );
        action.addEventListener('click', () => this.command({ t: 'learnArt', id }));
      }
      path.append(
        h(
          'li',
          { class: learned ? 'done' : reqOk ? 'open' : 'locked' },
          h('span', { class: 'step' }, String(i + 1)),
          h(
            'div',
            { class: 'step-card' },
            h(
              'div',
              { class: 'art-head' },
              h(
                'div',
                {},
                h('div', { class: 'art-name' }, art.name),
                h(
                  'div',
                  { class: 'art-school' },
                  `${art.weapon === 'sword' ? 'กระบี่' : 'มือเปล่า'}${art.requires ? ` · ต้องเรียน${getArt(art.requires).name}ก่อน` : ''}`,
                ),
              ),
              elementBadge(art.element),
              action,
            ),
            h('p', { class: 'note' }, art.desc),
            moves(id),
          ),
        ),
      );
    });
    wrap.append(path);
    return wrap;
  }

  /** ตำนานกระบี่ทั้งเก้า: the chapters read so far, the rest still sealed, and the stone's nine grooves. */
  private legendTab(): HTMLElement {
    const open = legendChapters(this.world.progress.quest.index);
    const sealed = LEGEND.length - open.length;
    // A sword's groove lights up once a chapter naming it has been read.
    const known = (name: string) => open.some((c) => c.title.includes(name));
    return h(
      'div',
      { class: 'tab-legend' },
      h('h4', {}, 'ตำนานกระบี่ทั้งเก้า'),
      h(
        'ol',
        { class: 'grooves' },
        ...NINE_SWORDS.map((s) =>
          h(
            'li',
            { class: known(s.name) ? 'lit' : '', title: known(s.name) ? `${s.lesson}` : 'ยังไม่รู้' },
            h('b', {}, String(s.n)),
            h('span', {}, known(s.name) ? s.name : '……'),
            h('small', {}, known(s.name) ? ZONES[s.zone].name : ''),
          ),
        ),
      ),
      ...open.map((c) => h('article', {}, h('h5', {}, c.title), ...c.text.map((t) => h('p', {}, t)))),
      sealed
        ? h(
            'p',
            { class: 'sealed' },
            `ยังมีอีก ${sealed} บทที่ถูกผนึก ทำภารกิจของผู้เฒ่าหลิวต่อเพื่ออ่านบทถัดไป`,
          )
        : h('p', { class: 'sealed' }, 'เจ้าอ่านตำนานจบแล้ว แต่ความลับของกระบี่เล่มที่สิบยังรออยู่'),
    );
  }

  private helpTab(): HTMLElement {
    const node = (g: string, cls: string, label: string) =>
      h('div', { class: `tri-node ${cls}` }, h('span', {}, g), h('small', {}, label));
    return h(
      'div',
      { class: 'tab-help' },
      h('h4', {}, 'สามกระบวนท่า'),
      h(
        'div',
        { class: 'triangle' },
        node('攻', 'k-attack', 'โจมตี'),
        h('div', { class: 'tri-arrow' }, 'ชนะ →'),
        node('破', 'k-break', 'ท่าทำลาย'),
        h('div', { class: 'tri-arrow' }, 'ชนะ →'),
        node('守', 'k-guard', 'ตั้งรับ'),
        h('div', { class: 'tri-arrow' }, 'ชนะ →'),
        node('攻', 'k-attack', 'โจมตี'),
      ),
      h(
        'ul',
        { class: 'rules' },
        h('li', {}, h('b', {}, '攻 โจมตี '), 'โดนทันที และขัดจังหวะศัตรูที่กำลังชาร์จท่าทำลาย'),
        h(
          'li',
          {},
          h('b', {}, '破 ท่าทำลาย '),
          'ต้องชาร์จก่อน ถ้าศัตรูตั้งรับอยู่ การ์ดจะแตกและมึนงง แต่ถ้าโดนตีระหว่างชาร์จจะถูกขัดจังหวะ',
        ),
        h(
          'li',
          {},
          h('b', {}, '守 ตั้งรับ '),
          'กดค้าง รับการโจมตีเหลือ 10% และทำให้ผู้โจมตีเสียจังหวะ แต่แพ้ท่าทำลาย และใช้พลังกาย',
        ),
        h(
          'li',
          {},
          'อ่านไอคอนเหนือหัวศัตรู แล้วตอบด้วยท่าที่ชนะ วงแดงบนพื้นคือท่าทำลายที่ตั้งรับไม่ได้ ให้ใช้วิชาตัวเบาหลบ',
        ),
      ),
      h('h4', {}, 'การฝึกปรือ'),
      h(
        'ul',
        { class: 'rules' },
        h(
          'li',
          {},
          'ไม่มีเลเวลตัวละคร แต้มฝึกปรือได้ทุก 30 วินาที (ยืนเฉย 30 แต้ม นั่งสมาธิ 120 แต้ม) จากการปราบศัตรู ภารกิจ และขณะออฟไลน์ (สูงสุด 4 ชั่วโมง)',
        ),
        h(
          'li',
          {},
          'สถานที่ฝึกวิชาพิเศษ (วงสีหยกบนแผนที่ M) เช่น น้ำตก ถ้ำ ยอดผา ป่าไผ่ อยู่ในนั้นได้แต้มฝึกปรือ ×1.5 ทั้งยืนและนั่งสมาธิ ถ้าที่นั้นเหมาะกับธาตุกำลังภายใน สำนัก หรือวิชาที่ใช้อยู่ ได้ ×2 · ระหว่างต่อสู้หรือขี่พาหนะจะหยุดชั่วคราว',
        ),
        h(
          'li',
          {},
          'ฝึกกระบวนท่าและลมปราณใช้เวลาจริง ฝึกได้ทีละอย่าง ค่าฝึก (แต้มฝึกปรือ) ถูกหักทีละน้อยทุก 5 วินาทีและแจ้งในแชท ถ้าแต้มไม่พอการฝึกจะหยุดรอ เปิดฝึกอัตโนมัติไว้จะฝึกขั้นถัดไปต่อเองจนตัน ระหว่างฝึกเล่นอย่างอื่นได้ ปิดเกมไปก็ยังฝึกต่อ ดูเวลาที่เหลือได้ใต้ตัวละครมุมซ้ายบน กดหยุดได้แต่ค่าฝึกที่เสียไปแล้วไม่คืน',
        ),
        h('li', {}, 'ใช้แต้มขัดเกลากระบวนท่าในแผงวิชายุทธ์ (K) และทะลวงกำลังภายในในแผงกำลังภายใน (N)'),
        h('li', {}, 'ระดับฝีมือขึ้นตามแต้มที่ใช้ไปทั้งหมด'),
      ),
      h('h4', {}, 'ปุ่มควบคุม'),
      h(
        'div',
        { class: 'keys' },
        ...[
          ['คลิกซ้าย', 'เดิน · เลือกศัตรูแล้วโจมตี · คุยกับ NPC'],
          ['R', '攻 โจมตีธรรมดา (เป้าหมายปัจจุบันหรือศัตรูที่ใกล้ที่สุด)'],
          ['T', '自 ออโต้ · เดินเองเพื่อปิด'],
          ['Esc', 'ปิดหน้าต่าง · เปิดหน้าตั้งค่า'],
          ['คลิกขวาค้าง / F', '守 ตั้งรับ'],
          ['1–8', 'กระบวนท่าของวิชาที่ใช้อยู่ · ท่าพลังใช้ความโกรธ (หลอดสีแดง)'],
          ['Space', '躍 กระโดด · กดซ้ำกลางอากาศได้อีกสองครั้ง (ครั้งที่สามคือเหยียบเมฆา)'],
          ['Q', '輕 พุ่งตัวหลบ'],
          [
            'กดทิศทางซ้ำสองครั้งกลางอากาศ',
            'พุ่งตัวกลางอากาศไปทางนั้น (ครั้งละหนึ่งต่อการกระโดด) · โจมตีและใช้ท่าได้ขณะลอยตัว',
          ],
          ['Tab', 'เลือกศัตรูที่ใกล้ที่สุด'],
          ['0', '龍 ประลองยุทธภพ · สมัคร ดูคู่รอบนี้ ตารางคะแนน และผลรอบ'],
          ['Z', 'เก็บอาวุธเข้าฝัก · ชักอาวุธออก (สู้เมื่อไหร่จะชักออกเอง)'],
          ['V', '坐 นั่งสมาธิ'],
          ['X', '御 ขี่กระบี่เหาะ · กดอีกครั้งเพื่อลง (ลงเองเมื่อสู้หรือโดนโจมตี)'],
          ['WASD', 'เดิน'],
          ['Shift ค้าง', '奔 วิ่งพันลี้ เร็วขึ้น 80% ใช้พลังกาย'],
          ['E', 'เดินไปคุยกับ NPC ที่ใกล้ที่สุด'],
          ['ล้อเมาส์ / + −', 'ซูมเข้า-ออก'],
          ['คลิกขวาลากซ้าย-ขวา · Ctrl+← → · [ ]', 'หมุนกล้องรอบตัวละคร'],
          ['C I K N J U H', 'ตัวละคร · กระเป๋า · วิชา · กำลังภายใน · สำนัก · สกิน · วิธีเล่น · Esc ปิด'],
          ['M', 'แผนที่ (มอนสเตอร์จุดแดง วงวาร์ป) · แท็บแผนที่โลก · คลิกมินิแมปก็เปิดได้'],
          ['Y B P L O', 'พาหนะ · ร้านหยก · ตั๋วยุทธภพ · ประลองบันได · ประลองยุทธ'],
        ].map(([k, v]) => h('div', {}, h('kbd', {}, k!), v!)),
      ),
      h('h4', {}, 'จอย'),
      h(
        'div',
        { class: 'keys' },
        ...[
          ['สติ๊กซ้าย / D-pad', 'เดิน'],
          ['A (✕)', '躍 กระโดด · บนพาหนะเหาะกดค้างเพื่อบินขึ้น'],
          ['B (○)', '輕 พุ่งตัวหลบ · บนพาหนะเหาะกดค้างเพื่อบินลง'],
          ['X (□)', '攻 โจมตีธรรมดา'],
          ['Y (△)', 'คุยกับ NPC ที่ใกล้ที่สุด'],
          ['LT (L2) ค้าง', '守 ตั้งรับ'],
          ['RT (R2) ค้าง', '奔 วิ่งพันลี้'],
          ['LB (L1) + A B X Y', 'กระบวนท่า 1–4'],
          ['RB (R1) + A B X Y', 'กระบวนท่า 5–8'],
          ['สติ๊กขวา', 'ปัดขึ้น-ลงซูม · ซ้าย-ขวาหมุนกล้อง'],
          ['L3 / R3', 'เลือกศัตรูที่ใกล้ที่สุด · เก็บ-ชักอาวุธ'],
          ['Start / Back', 'Esc (ปิดหน้าต่าง · ตั้งค่า) · แผนที่'],
        ].map(([k, v]) => h('div', {}, h('kbd', {}, k!), v!)),
      ),
    );
  }
}
